import { NewsModel } from "../../models/NewsModel";
import React, { useId, useState } from "react";
import {
    Button, Caption1,
    Dialog, DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Input,
    Label, Spinner, Textarea
} from "@fluentui/react-components";
import { uploadImageToDatabase } from "../../handlers/DatabaseHandler";
import {addNewsItem, updateNewsItem} from "../../handlers/NewsHandler";
import {Add16Filled} from "@fluentui/react-icons";

type Props = {
    newsItem?: NewsModel;
    onClose: () => void;
};

const NewsDialog: React.FC<Props> = ({ newsItem: propItem, onClose }) => {
    const isEdit = propItem !== undefined;

    const initialItem: NewsModel = propItem || {
        id: -1,
        created_at: new Date(),
        title: "",
        description: "",
        category: "",
        image_url: "",
        link: "",
        date: new Date()
    };

    const [newsItem, setNewsItem] = useState<NewsModel>(initialItem);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const baseId = useId();

    const fieldIds = {
        title: `${baseId}-title`,
        description: `${baseId}-description`,
        category: `${baseId}-category`,
        image_url: `${baseId}-image_url`,
        link: `${baseId}-link`,
    } as const;

    const handleImageUpload = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setIsUploading(true);

        const file = e.target.files?.[0];
        if (!file) return;

        const url = await uploadImageToDatabase(file);

        setIsUploading(false);

        if (url) {
            setNewsItem(prev => ({
                ...prev,
                image_url: url
            }));
        }
    };

    const saveNewsItem = async () => {
        setIsLoading(true);
        const result = isEdit ? await updateNewsItem(newsItem) : await addNewsItem(newsItem);
        setIsLoading(false);

        if (!result) return;

        if (result.error) {
            setError(result.error);
            return;
        }

        setIsOpen(false);

        onClose();
    };

    return (
        <Dialog open={isOpen}>
            <DialogTrigger disableButtonEnhancement>
                <Button as={`a`}
                        className={`button`}
                        onClick={ () => setIsOpen(true) } >
                    { isEdit ? <>Bewerken</> : <>Nieuwsitem toevoegen <Add16Filled/></> }
                </Button>
            </DialogTrigger>

            <DialogSurface className="dialog">
                <DialogBody>
                    { error && <Caption1>{ error }</Caption1> }
                    <DialogTitle>
                        Nieuwsitem {isEdit ? "bewerken" : "toevoegen"}
                    </DialogTitle>

                    <DialogContent>
                        <form className="flex flex-column flex-gap-medium">
                            <div className={`flex flex-column`}>
                                <Label htmlFor={fieldIds.title}>Titel</Label>
                                <Input
                                    id={fieldIds.title}
                                    value={newsItem.title}
                                    onChange={(e) =>
                                        setNewsItem({ ...newsItem, title: e.target.value })
                                    }
                                />
                            </div>

                            <div className={`flex flex-column`}>
                                <Label htmlFor={fieldIds.description}>Beschrijving</Label>
                                <Textarea
                                    id={fieldIds.description}
                                    value={newsItem.description ?? ""}
                                    onChange={(e) =>
                                        setNewsItem({ ...newsItem, description: e.target.value })
                                    }
                                />
                            </div>

                            <div className={`flex flex-column`}>
                                <Label htmlFor={fieldIds.category}>Categorie</Label>
                                <Input
                                    id={fieldIds.category}
                                    value={newsItem.category ?? ""}
                                    onChange={(e) =>
                                        setNewsItem({ ...newsItem, category: e.target.value })
                                    }
                                />
                            </div>

                            <div className={`flex flex-column`}>
                                <Label htmlFor={fieldIds.link}>Link</Label>
                                <Input
                                    id={fieldIds.link}
                                    value={newsItem.link ?? ""}
                                    onChange={(e) =>
                                        setNewsItem({ ...newsItem, link: e.target.value })
                                    }
                                />
                            </div>

                            <div className={`flex flex-column`}>
                                <Label htmlFor={fieldIds.image_url}>Afbeelding</Label>
                                <Input
                                    id={fieldIds.image_url}
                                    value={newsItem.image_url ?? ""}
                                    onChange={(e) =>
                                        setNewsItem({ ...newsItem, image_url: e.target.value })
                                    }
                                />
                            </div>
                            <div className={`file-input-wrapper`} >
                                {
                                    isUploading ?
                                        <>
                                            <Spinner size={`small`} />
                                            <p>Afbeelding wordt geüpload...</p>
                                        </>
                                        :
                                        <>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                            />
                                            <svg className="file-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12M8 8l4-4 4 4" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <p>{newsItem.image_url ? `Huidige afbeelding vervangen` : `Upload een afbeelding`}</p>
                                        </>

                                }
                            </div>
                        </form>
                    </DialogContent>

                    <DialogActions>
                        <Button as={`a`} className="button" onClick={() => setIsOpen(false)}>
                            Sluiten
                        </Button>
                        <Button as={`a`} className="button" appearance="primary" onClick={saveNewsItem} disabled={isLoading}>
                            Opslaan
                        </Button>
                        {
                            isLoading && <Spinner size={`extra-tiny`}/>
                        }
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default NewsDialog;